import type { RouteLocationNormalized } from 'vue-router';
import { defineStore } from 'pinia';
import {
  DEFAULT_ROUTE,
  DEFAULT_ROUTE_NAME,
  // DEFAULT_ROUTE_PRO,
  // DEFAULT_ROUTE_NAME_PRO,
  REDIRECT_ROUTE_NAME,
  DEV_ROUTE_NAME,
  NOT_FOUND_ROUTE_NAME,
} from '@/router/constants';
import SuperTask from '@/utils/superTask';
import { electronRemoveSuffix, isElectron } from '@/utils/electronFunc';
import { TabBarState, TagProps } from './types';

const formatTag = (
  route: RouteLocationNormalized,
  fromTag: TagProps | undefined
): TagProps => {
  const { name, meta, fullPath, query } = route;
  return {
    title: String(meta.title) || '',
    name: String(name),
    fullPath,
    query,
    ignoreCache: meta.ignoreCache,
    meta,
    fromTag,
  };
};

const BAN_LIST = [REDIRECT_ROUTE_NAME, DEV_ROUTE_NAME];

const useAppStore = defineStore('tabBar', {
  state: (): TabBarState => ({
    cacheTabList: new Set([]),
    tagList: [],
    cacheDeleteTag: undefined, // 上一个删除的tag，用户back回上一个路由fromTag
    lastDeleteTag: undefined, // 上一个删除的tag，用于剔除缓存，用一次则置空
    paymentMap: null,
    pageLoading: false,
    reloadFullUrl: undefined,
    componentKeyMap: {},
  }),

  getters: {
    getTabList(): TagProps[] {
      return this.tagList;
    },
    getCacheList(): string[] {
      return Array.from(this.cacheTabList);
    },
    getLastReloadFullUrl(): string | undefined {
      return this.reloadFullUrl;
    },
  },

  actions: {
    getDefaultInfo(paymentMap: any) {
      // const defaultRoute = paymentMap.professional
      //   ? DEFAULT_ROUTE
      //   : DEFAULT_ROUTE_PRO;
      // const defaultRouteName = paymentMap.professional
      //   ? DEFAULT_ROUTE_NAME
      //   : DEFAULT_ROUTE_NAME_PRO;
      const defaultRoute = DEFAULT_ROUTE;
      const defaultRouteName = DEFAULT_ROUTE_NAME;
      return {
        defaultRoute,
        defaultRouteName,
      };
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = isElectron() ? false : pageLoading;
    },
    setReloadFullUrl(fullUrl: string) {
      this.reloadFullUrl = fullUrl;
    },
    // 初始化必须
    setPayment(currentUser: any) {
      const paymentMap = {
        // 旗舰版
        flagShip: currentUser.data?.enablePaymentServiceMap['旗舰版'],
        // 专业版
        professional: currentUser.data?.enablePaymentServiceMap['专业版'],
      };
      if (!paymentMap.flagShip && !paymentMap.professional) {
        paymentMap.professional = 1;
      }
      const info = this.getDefaultInfo(paymentMap);
      this.emptyTabList();
      this.paymentMap = paymentMap;
      this.addCacheTag(info.defaultRoute as TagProps);
    },
    updateTabList(
      route: RouteLocationNormalized,
      from: RouteLocationNormalized,
      addIdx?: number
    ) {
      if (BAN_LIST.includes(route.name as string)) return;
      const fromTag =
        this.tagList.find((item) => item.fullPath === from?.fullPath) ||
        // 如果是404页面，一般是重定向到404页面的，而发起重定向的页面（cacheDeleteTag）会被关闭，在tagList中找不到，所以需要在cacheDeleteTag.fromTag拿到跳进无权限页面的路由，back回去
        (route.name === NOT_FOUND_ROUTE_NAME &&
        this.cacheDeleteTag?.fromTag &&
        this.tagList.find(
          (item) => item.fullPath === this.cacheDeleteTag?.fromTag?.fullPath
        )
          ? this.cacheDeleteTag.fromTag
          : undefined);
      const newTag = formatTag(route, fromTag);
      if (addIdx !== undefined && addIdx > -1) {
        this.tagList.splice(addIdx, 0, newTag);
      } else {
        this.tagList.push(newTag);
      }
      if (!route.meta.ignoreCache) {
        this.addCacheTag(newTag);
      }
    },
    setLastDeleteTag(tag: TagProps) {
      this.lastDeleteTag = tag;
    },

    checkDelTag() {
      if (this.lastDeleteTag) {
        const { name } = this.lastDeleteTag;
        if (!this.tagList.filter((tag: TagProps) => tag.name === name).length) {
          this.deleteCacheByName(name);
        }
        this.deleteCacheKey(this.lastDeleteTag);
        this.lastDeleteTag = undefined;
      }
    },
    deleteTag(tag: TagProps) {
      return new Promise<number>((resolve, reject) => {
        this.checkBeforeClose(tag)
          .then(() => {
            const idx = this.tagList.findIndex(
              (item) => item.fullPath === tag.fullPath
            );
            if (idx > -1) {
              this.cacheDeleteTag = this.tagList[idx]; // 记录上一个删除的tag，为了保存他的fromTag，在back时能回到他的来源tag
              this.setLastDeleteTag(tag);
              this.tagList.splice(idx, 1);
            }
            resolve(idx);
          })
          .catch(() => {
            reject();
          });
      });
    },
    deleteCache(tag: TagProps) {
      // 关闭tag时检查是否存在多个相同name的tag，如果有多个则不把该routeName踢出缓存
      if (!this.hasMulCacheTagByName(tag.name)) {
        this.deleteCacheByName(tag.name);
      }
    },
    // 不要直接调用这个删除name，通过调用deleteCacheTag删除tag
    // 调用一下方法将去除同name下的所有fullPath缓存
    deleteCacheByName(name: string) {
      this.cacheTabList.delete(name);
      delete this.componentKeyMap?.[name];
    },

    // 添加tag缓存
    addCacheTag(tag: TagProps) {
      if (!tag?.ignoreCache && !tag?.meta?.ignoreCache) {
        this.cacheTabList.add(tag.name as string);
        this.setCacheKey(tag);
      }
    },

    // 删除tag缓存
    deleteCacheTag(tag: TagProps) {
      this.deleteCache(tag);
      this.deleteCacheKey(tag);
    },

    // 新增一个缓存key
    newCacheKey(fullPath: string) {
      return `${fullPath}_keep_key_${Date.now()}`;
    },

    // 获取keep-alive的组件缓存key
    getComponentKey(route: RouteLocationNormalized) {
      const { name, fullPath } = route;
      // 如果组件被销毁，返回一个不同的key来强制重新创建组件
      if (!this.componentKeyMap?.[name as string]?.[fullPath]) {
        this.setCacheKey(route as any);
      }
      return this.componentKeyMap?.[name as string]?.[fullPath];
    },

    // 设置组件缓存key
    setCacheKey(tag: TagProps) {
      const { name, fullPath } = tag;
      if (!this.componentKeyMap[name]) {
        this.componentKeyMap[name] = {};
      }
      this.componentKeyMap[name][fullPath] = this.newCacheKey(fullPath);
    },

    // 删除组件缓存key
    deleteCacheKey(tag: TagProps) {
      const { name, fullPath } = tag;
      delete this.componentKeyMap?.[name]?.[fullPath];
    },

    // 根据tagName检查当前tagList中是否有多个
    hasMulCacheTagByName(name: string) {
      return (
        this.tagList.filter((tag: TagProps) => tag.name === name).length > 1
      );
    },
    // 详情页可能存在多个routeName一样的Tag，通过fullPath关闭
    deleteTagByFullPath(fullPath: string) {
      const tag = this.tagList.find(
        (item) => item.fullPath === electronRemoveSuffix(fullPath)
      );
      if (!tag) {
        return Promise.resolve();
      }
      return this.deleteTag(tag as TagProps);
    },
    // 根据fullPush获取tag
    getTagByFullPush(fullPath: string) {
      return this.tagList.find(
        (item) => item.fullPath === electronRemoveSuffix(fullPath)
      );
    },
    freshTabList(tags: TagProps[]) {
      return new Promise<void>((resolve) => {
        const newFullPathList = tags.map((e) => e.fullPath);
        const delTags = this.tagList.filter(
          (tag) => !newFullPathList.includes(tag.fullPath)
        );
        this.batchDelTags(delTags).finally(() => {
          this.resetCacheTabList();
          resolve();
        });
      });
    },
    resetTabList() {
      if (!this.paymentMap) {
        return Promise.resolve();
      }
      const info = this.getDefaultInfo(this.paymentMap);
      return new Promise<void>((resolve) => {
        this.batchDelTags(this.tagList).finally(() => {
          if (!this.tagList.find((tag) => tag.name === info.defaultRouteName)) {
            this.tagList.unshift(info.defaultRoute);
          }
          this.resetCacheTabList();
          resolve();
        });
      });
    },
    resetCacheTabList() {
      this.cacheTabList.clear();
      this.componentKeyMap = {};
      // 要先判断ignoreCache
      this.tagList
        .filter((el) => !el.ignoreCache)
        // .map((el) => el.name)
        .forEach((x) => this.addCacheTag(x));
    },
    emptyTabList() {
      this.tagList = [];
      this.cacheTabList.clear();
      this.cacheDeleteTag = undefined;
      this.lastDeleteTag = undefined;
      this.componentKeyMap = {};
    },
    // 关闭tag前检查
    checkBeforeClose(tag: TagProps) {
      const { closeTagCallback } = tag;
      if (closeTagCallback) {
        // 关闭tag前检查是否存在callback，并执行
        return new Promise<void>((resolve, reject) => {
          const done = (closed = true) => {
            if (closed) {
              resolve();
            } else {
              reject();
            }
          };
          closeTagCallback(done);
        });
      }
      return Promise.resolve();
    },
    // 批量删除tag，异步并发一个一个执行
    batchDelTags(tags: TagProps[]) {
      const superTask = new SuperTask(1);
      tags.forEach((tag) => {
        superTask
          .add(() => this.deleteTag(tag))
          .catch(() => {
            // log
          });
      });
      // 等待所有任务完成
      return superTask.waitForAllTasks();
    },
    // 批量根据路由名称删除tag
    byRouterNameBatchDelTags(delNames: Array<string>) {
      if (!delNames?.length) return;
      this.tagList = this.tagList.filter(
        (tag: TagProps) => !delNames.includes(tag.name)
      );
      delNames.forEach((name: string) => this.deleteCacheByName(name));
    },
  },
});

export default useAppStore;
